/**
 * CORE-BI — the Shop Profile page's slice of shop culture
 *
 * Converts the Shop Profile form's camelCase values into the snake_case
 * keys stored inside `shop_profile.shop_culture`, and *only* those keys.
 *
 * The important word is "only". Before this module existed the page built
 * a complete replacement object and wrote it over the column, deleting
 * every key belonging to any other capability — the board optimizer's
 * settings, preference-learning settings, and CORE-BN's front-of-house
 * policy — on every ordinary "Save Profile". The output here is a PATCH,
 * applied through `patchShopCulture`, so keys this form does not own
 * survive whether or not this code has ever heard of them.
 *
 * Purity: no I/O, no clock, no React.
 */

import { SHOP_CULTURE_DEFAULTS } from './shopCultureDefaults';
import {
  patchShopCulture,
  SHOP_PROFILE_OWNED_CULTURE_KEYS,
  type ShopCultureObject,
  type ShopCulturePatch,
} from './shopCulturePatch';

/**
 * Defensive normalizer for the Shop Culture "Skill Tier Gap for Ticket
 * Splitting" slider.
 *
 * History:
 *   - Migration 20251024021603 created `shop_profile.shop_culture` jsonb
 *     with column DEFAULT `{"specialist_gap_splitting": 15, ...}` when
 *     the slider's product range was 0-100.
 *   - The slider was later tightened to 0-10 in the React zod schema.
 *   - Migration 20251122212334 cleaned EXISTING rows from 15 → 5 but did
 *     NOT change the column DEFAULT, so every shop created after Nov 2025
 *     still gets `15` written by the column default. The form's `?? 5`
 *     null-coalesce passed `15` through unchanged → failed zod `max(10)`
 *     → silent validation abort → "Save Profile button stopped saving".
 *
 * Rules:
 *   - null / undefined / non-number / NaN → 3 (the current product default)
 *   - exactly 15                          → 3 (legacy column DEFAULT —
 *                                              clearly default-created, so reset)
 *   - < 0                                 → 0 (clamp)
 *   - > 10                                → 10 (clamp; preserves an
 *                                              operator-set high value that
 *                                              is not the legacy 15)
 *   - otherwise                           → unchanged
 *
 * Used at the form's load sites AND at save-payload assembly, so a stale
 * legacy value can never block a save and a state regression can never
 * write an invalid value back to the DB.
 *
 * Lives here rather than in the page because it normalises a stored
 * shop-culture value, and the page is not the authority on that.
 */
export function normalizeSpecialistGapSplitting(raw: unknown): number {
  // The product default now lives in the ONE shared table (CORE-BI R4).
  const fallback = SHOP_CULTURE_DEFAULTS.specialist_gap_splitting;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return fallback;
  if (raw === 15) return fallback;
  if (raw < 0) return 0;
  if (raw > 10) return 10;
  return raw;
}

/** The outdoor-weather block as the Shop Profile form holds it. */
export interface ShopProfileOutdoorWeatherValues {
  enabled?: boolean;
  minOutdoorTempF?: number;
  blockWhenRaining?: boolean;
  blockWhenSnowing?: boolean;
  blockWhenStorms?: boolean;
}

/**
 * The Shop Profile form's culture values, in the shape the page's zod
 * schema infers. Every field is optional so a partially-populated form
 * cannot throw on the way to a save.
 */
export interface ShopProfileCultureValues {
  willingnessToReassign?: number;
  specialistGapSplitting?: number;
  workloadBalancePreference?: number;
  comebackNudge?: number;
  willingnessRollPastEod?: number;
  presentVehiclePriority?: number;
  ticketSplittingEnabled?: boolean;
  minSplitMinutes?: number;
  crossTechFitnessThreshold?: number;
  proficiencyGapThreshold?: number;
  waiterTimeFlexMin?: number;
  noShowGraceThresholdMin?: number;
  /** CORE-BI GAP-001: minutes before end of day at which a finish is flagged as EOD risk. */
  eodRiskThresholdMin?: number;
  allowFutureDayPullForward?: boolean;
  /** oct20e (FD-2026-10-24-A): Core automatic capacity recovery (true, the default) or manual suggestion mode (false). */
  capacityRecoveryAutomatic?: boolean;
  /** Core-100 R5 (CORE-BI x CORE-I): weigh demonstrated diagnostic competency when ranking diagnostic work. */
  diagnosticWeightEnabled?: boolean;
  outdoorWeatherSettings?: ShopProfileOutdoorWeatherValues | null;
}

/**
 * Build the patch of shop-culture keys the Shop Profile form owns.
 *
 * Returns an empty patch when the form has no culture values at all. That
 * is deliberate: "the form had nothing to say" must leave the stored
 * object exactly as it was. The previous code wrote `shop_culture: null`
 * in that situation, which erased the entire object for every capability
 * at once.
 *
 * `outdoor_weather_settings` is written as `null` rather than removed
 * when the block is absent, preserving the column's existing convention —
 * the load path reads a null there as "weather gating not configured".
 */
export function buildShopProfileCulturePatch(
  values: ShopProfileCultureValues | null | undefined,
): ShopCulturePatch {
  if (!values || typeof values !== 'object') return {};

  const weather = values.outdoorWeatherSettings;

  return {
    willingness_to_reassign: values.willingnessToReassign,
    // Defence in depth: the zod resolver already gated min(0).max(10)
    // before this runs, but normalise once more so a future state-shape
    // regression can never persist an invalid value.
    specialist_gap_splitting: normalizeSpecialistGapSplitting(
      values.specialistGapSplitting,
    ),
    workload_balance_preference: values.workloadBalancePreference,
    comeback_nudge: values.comebackNudge,
    willingness_roll_past_eod: values.willingnessRollPastEod,
    present_vehicle_priority: values.presentVehiclePriority,
    ticket_splitting_enabled: values.ticketSplittingEnabled,
    min_split_minutes: values.minSplitMinutes,
    cross_tech_fitness_threshold: values.crossTechFitnessThreshold,
    proficiency_gap_threshold: values.proficiencyGapThreshold,
    waiter_time_flex_min: values.waiterTimeFlexMin,
    // Bucket 5 §5.5.4 — no-show grace threshold.
    no_show_grace_threshold_min: values.noShowGraceThresholdMin,
    // Core-100 R4 CORE-BI (GAP-001) — was consumed by the EOD-risk detector with no control.
    eod_risk_threshold_min: values.eodRiskThresholdMin,
    // Core-100 CORE-BO (GAP-004) — future-day pull-forward policy. oct20e (FD-2026-10-24-A; CORE-BO :74, :387 as
    // amended): permitted by default for physically present vehicles; only the shop's explicit `false` restricts it.
    allow_future_day_pull_forward: values.allowFutureDayPullForward !== false,
    // oct20e (FD-2026-10-24-A; CORE-BO :59, :93 as amended): automatic capacity recovery is the Core default; only the
    // shop's explicit `false` selects manual suggestion/approval mode.
    capacity_recovery_automatic: values.capacityRecoveryAutomatic !== false,
    // Core-100 R5 (CORE-BI x CORE-I) — absent = false (today's ranking).
    diagnostic_weight_enabled: values.diagnosticWeightEnabled === true,
    outdoor_weather_settings: weather
      ? {
          enabled: weather.enabled,
          min_outdoor_temp_f: weather.minOutdoorTempF,
          block_when_raining: weather.blockWhenRaining,
          block_when_snowing: weather.blockWhenSnowing,
          block_when_storms: weather.blockWhenStorms,
        }
      : null,
  };
}

/**
 * Apply the Shop Profile form's culture values to the object currently
 * stored for the shop.
 *
 * `existingCulture` MUST come from a read performed as part of the same
 * save. Using a value captured at page load would reopen the defect for
 * any key written by another surface while the page was open.
 */
export function applyShopProfileCulture(
  existingCulture: unknown,
  values: ShopProfileCultureValues | null | undefined,
): ShopCultureObject {
  return patchShopCulture(existingCulture, buildShopProfileCulturePatch(values));
}

/** Re-exported so callers need only one import for the safe-save path. */
export { SHOP_PROFILE_OWNED_CULTURE_KEYS };
